import { useState } from 'react';
import { HelpCircle, X, Lightbulb, ChevronRight } from 'lucide-react';
import { Button } from './button';

// Simple tooltip for inline help
export const HelpTooltip = ({ content, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="text-gray-400 hover:text-[#1E3A5F] transition-colors"
        aria-label="Help"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      {isOpen && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1E3A5F] text-white text-xs rounded-lg shadow-lg max-w-xs whitespace-normal">
          {content}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1E3A5F]" />
        </div>
      )}
    </div>
  );
};

// Empty state with guidance
export const EmptyStateGuide = ({ 
  icon: Icon, 
  title, 
  description, 
  actionLabel, 
  onAction,
  tips = [],
  testId
}) => {
  return (
    <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-8 text-center" data-testid={testId}>
      <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">{title}</h3>
      <p className="text-gray-600 mb-4 max-w-md mx-auto">{description}</p>
      
      {tips.length > 0 && (
        <div className="bg-amber-50 rounded-lg p-4 mb-4 max-w-md mx-auto text-left">
          <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
            <Lightbulb className="w-4 h-4" />
            Quick Tips
          </div>
          <ul className="space-y-1">
            {tips.map((tip, idx) => (
              <li key={idx} className="text-sm text-amber-800 flex items-start gap-2">
                <ChevronRight className="w-3 h-3 mt-1 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {actionLabel && onAction && (
        <Button onClick={onAction} className="btn-primary" data-testid={`${testId}-action`}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

// Feature highlight card for onboarding
export const FeatureHighlight = ({ 
  icon: Icon, 
  title, 
  description, 
  color = 'blue',
  onDismiss,
  learnMoreUrl
}) => {
  const colors = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', title: 'text-blue-800' },
    green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', title: 'text-green-800' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', title: 'text-purple-800' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', title: 'text-amber-800' },
  };
  
  const c = colors[color] || colors.blue;

  return (
    <div className={`${c.bg} ${c.border} border rounded-lg p-4 relative`}>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${c.bg}`}>
          <Icon className={`w-5 h-5 ${c.icon}`} />
        </div>
        <div className="flex-1">
          <h4 className={`font-semibold ${c.title}`}>{title}</h4>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
          {learnMoreUrl && (
            <a href={learnMoreUrl} className="text-sm text-blue-600 hover:underline mt-2 inline-block">
              Learn more →
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Quick action suggestion
export const QuickActionCard = ({ 
  icon: Icon, 
  title, 
  description, 
  onClick,
  variant = 'default' // default, success, warning
}) => {
  const variants = {
    default: 'border-gray-200 hover:border-[#1E3A5F] hover:bg-[#FFEDDF]',
    success: 'border-green-200 bg-green-50 hover:bg-green-100',
    warning: 'border-amber-200 bg-amber-50 hover:bg-amber-100',
  };

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${variants[variant]}`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-[#1E3A5F]" />
        <div>
          <h4 className="font-medium text-[#1E3A5F]">{title}</h4>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
      </div>
    </button>
  );
};

// Onboarding checklist
export const OnboardingChecklist = ({ items, onDismiss }) => {
  const completed = items.filter(item => item.completed).length;
  const progress = (completed / items.length) * 100;

  return (
    <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-[#1E3A5F]">Getting Started</h3>
        {onDismiss && (
          <button onClick={onDismiss} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-gray-100 rounded-full mb-4 overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#1E3A5F] to-[#1E3A5F] rounded-full transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-gray-500 mb-4">{completed} of {items.length} completed</p>
      
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div
            key={idx}
            className={`flex items-center gap-3 p-2 rounded-lg ${item.completed ? 'bg-green-50' : 'bg-gray-50'}`}
          >
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              item.completed 
                ? 'bg-green-500 border-green-500 text-white' 
                : 'border-gray-300'
            }`}>
              {item.completed && <span className="text-xs">✓</span>}
            </div>
            <span className={`text-sm ${item.completed ? 'text-gray-500 line-through' : 'text-[#1E3A5F]'}`}>
              {item.label}
            </span>
            {!item.completed && item.onClick && (
              <button
                onClick={item.onClick}
                className="ml-auto text-xs text-blue-600 hover:underline"
              >
                Do it →
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Page intro banner for first-time visitors
export const PageIntroBanner = ({ 
  title, 
  description, 
  icon: Icon,
  features = [],
  onDismiss,
  dismissKey // localStorage key to remember dismissal
}) => {
  const [dismissed, setDismissed] = useState(() => {
    if (dismissKey) {
      return localStorage.getItem(`help_dismissed_${dismissKey}`) === 'true';
    }
    return false;
  });

  const handleDismiss = () => {
    if (dismissKey) {
      localStorage.setItem(`help_dismissed_${dismissKey}`, 'true');
    }
    setDismissed(true);
    onDismiss?.();
  };

  if (dismissed) return null;

  return (
    <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2563eb] text-white rounded-xl p-6 mb-6 relative">
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-white/70 hover:text-white"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-start gap-4">
        {Icon && (
          <div className="p-3 bg-white/10 rounded-lg">
            <Icon className="w-8 h-8" />
          </div>
        )}
        <div className="flex-1">
          <h2 className="text-xl font-bold mb-2">{title}</h2>
          <p className="text-white/90 mb-4">{description}</p>
          
          {features.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {features.map((feature, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-white/10 rounded-full text-sm"
                >
                  {feature}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpTooltip;
